import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { AppBar, Toolbar } from '@mui/material'

import { Avatar } from '@dhl/components'
import { Logo } from '@dhl/logo'
import { Language } from '@dhl/i18n'
import { Menu, User as UserIcon } from '@dhl/icons'
import { Box, Button, IconButton, useTheme } from '@dhl/theme'

import { User } from '~/account/model/reducer'
import { links } from '~/links'
import { ReduxState } from '~/redux/createRootReducer'
import { selectUser } from '~/selectors/selectUser'
import { locales } from '~/i18n'
import { WhatsNewButton } from '~/components/whatsNew/WhatsNew'
import { getConfig, isFeatureEnabled } from '~/config'

type Props = {
  hideNavigation: boolean
  onClick: () => void
  isLoggedIn: boolean
  isParcelShop: boolean
  user: User
  drawerWidth: number
  onProfileClick: () => void
}

const HeaderComponent: FC<Props> = ({
  onClick,
  hideNavigation,
  isLoggedIn,
  isParcelShop,
  user,
  drawerWidth,
  onProfileClick,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    device: { isMobile },
  } = useTheme()

  const whatsNewEnabled = isFeatureEnabled('whatsNew')
  const { navigation } = getConfig()
  const isWhatsNewEnabled = (whatsNewEnabled && !isParcelShop) || window.Cypress
  const useLegacyPwaMenu = navigation === 'legacy' || window.Cypress
  const initials = [user?.name?.firstName || '', user?.name?.lastName || '']
    .map((n) => n[0])
    .join('')
    .toUpperCase()

  return (
    <AppBar
      sx={(theme) => ({
        backgroundColor: theme.palette.secondary.main,
        zIndex: theme.zIndex.drawer + 1,
        border: 0,
      })}
      position="fixed"
    >
      <Toolbar variant="dense">
        {isMobile && (
          <IconButton edge="start" onClick={onClick} size="large">
            <Menu sx={{ color: '#333' }} />
          </IconButton>
        )}

        <Box
          sx={{
            minWidth: (theme) => (isMobile ? '120px' : `calc(${drawerWidth}px + ${theme.spacing(2)})`),
            '& > a': {
              display: 'flex',
            },
          }}
        >
          <RouterLink to="/">
            <Logo width={112} height={24} />
          </RouterLink>
        </Box>

        <Box flexGrow={1} />

        {isLoggedIn && !hideNavigation && (
          <>
            {isWhatsNewEnabled && <WhatsNewButton />}
            {useLegacyPwaMenu ? (
              <IconButton onClick={onProfileClick} data-test="link-button" size="large">
                <UserIcon color="primary" />
              </IconButton>
            ) : (
              <Avatar onClick={onProfileClick} data-test="link-button" src="/receiver-profile-api/profile/picture">
                {initials}
              </Avatar>
            )}
          </>
        )}

        {!isLoggedIn && (
          <>
            <Language locales={locales()} />
            {isWhatsNewEnabled && <WhatsNewButton />}
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(links.accountSignInLink())}
              data-test="login-button"
              sx={{
                whiteSpace: 'nowrap',
              }}
            >
              {t('login')}
            </Button>
          </>
        )}
      </Toolbar>
    </AppBar>
  )
}

export const Header = connect((s: ReduxState) => ({
  user: selectUser(s).user,
}))(HeaderComponent)
